import type React from "react";
import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";

import { useCurrentRelease } from "@/common";
import { useNotification } from "@/common/notifications";
import {
  createReleaseVariable,
  updateReleaseVariable,
  deleteReleaseVariable,
  releaseService,
} from "@/app/providers/services";

import type { Variable, VariableChange, VariableValidationError } from "./variablesTypes";
import { normalizeVariables } from "./normalizeVariables";

//#region Types

interface VariablesContextValue {
  variables: Variable[];
  addVariable: () => void;
  updateVariable: (variableId: string, updates: { key?: string; value?: string }) => void;
  deleteVariable: (variableId: string) => void;
  saveVariable: (variableId: string) => Promise<{ success: boolean; error?: string }>;
  isDirty: boolean;
  changes: VariableChange[];
  getVariableIsDirty: (variableId: string) => boolean;
  getVariableErrors: (variableId: string) => VariableValidationError[];
  validationErrors: VariableValidationError[];
  hasValidationErrors: boolean;
  isSavingVariableId: string | null;
  isLoading: boolean;
}

//#endregion

//#region Helpers

const KEY_REGEX = /^[a-zA-Z0-9._-]+$/;

const validateKey = (key: string): string | null => {
  if (!key || key.trim() === "") {
    return "Key is required";
  }
  if (!KEY_REGEX.test(key)) {
    return "Key cannot contain spaces or special characters";
  }
  return null;
};

const validateValue = (value: string): string | null => {
  if (value === undefined || value === null || value.trim() === "") {
    return "Value is required";
  }
  return null;
};

//#endregion

//#region Context

const VariablesContext = createContext<VariablesContextValue | null>(null);

export const useVariables = (): VariablesContextValue => {
  const context = useContext(VariablesContext);
  if (!context) {
    throw new Error("useVariables must be used within a VariablesProvider");
  }
  return context;
};

//#endregion

//#region Provider

interface VariablesProviderProps {
  children: React.ReactNode;
}

export const VariablesProvider = ({ children }: VariablesProviderProps) => {
  const release = useCurrentRelease();
  const notify = useNotification();
  const { t } = useTranslation();

  const [variables, setVariables] = useState<Variable[]>([]);
  const [savedVariables, setSavedVariables] = useState<Variable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingVariableId, setSavingVariableId] = useState<string | null>(null);

  const loadedReleaseIdRef = useRef<string | null>(null);
  // Tracks whether release.variables has been loaded for the current release to avoid
  // repeatedly syncing from a changing array reference and triggering render loops.
  const loadedVariablesFieldRef = useRef(false);
  const isDirtyRef = useRef(false);

  const cloneVariables = useCallback((vars: Variable[]): Variable[] => vars.map((variable) => ({ ...variable })), []);
  const syncVariablesFromRelease = useCallback(
    (rawVariables: unknown) => {
      const vars = normalizeVariables(rawVariables);
      setVariables(cloneVariables(vars));
      setSavedVariables(cloneVariables(vars));
    },
    [cloneVariables],
  );

  const fetchReleaseVariables = useCallback(
    async (releaseId: string, options?: { showLoading?: boolean }) => {
      const showLoading = options?.showLoading ?? true;
      if (showLoading) {
        setIsLoading(true);
      }
      try {
        const latestRelease = await releaseService.getOne(releaseId);
        const releaseVariablesRaw = (latestRelease as unknown as { variables?: unknown }).variables;

        if (loadedReleaseIdRef.current === releaseId) {
          loadedVariablesFieldRef.current = true;
          if (!isDirtyRef.current) {
            syncVariablesFromRelease(releaseVariablesRaw);
          }
        }
      } catch (error) {
        notify.error(t("variables.notifications.load_failed"));
      } finally {
        if (showLoading && loadedReleaseIdRef.current === releaseId) {
          setIsLoading(false);
        }
      }
    },
    [notify, syncVariablesFromRelease, t],
  );

  const isDirty = useMemo(() => {
    const savedMap = new Map(savedVariables.map((v) => [v.id, v]));
    if (variables.length !== savedVariables.length) {
      return true;
    }
    for (const v of variables) {
      const saved = savedMap.get(v.id);
      if (!saved || saved.key !== v.key || saved.value !== v.value) {
        return true;
      }
    }
    return false;
  }, [variables, savedVariables]);

  const changes = useMemo(() => {
    const savedMap = new Map(savedVariables.map((v) => [v.id, v]));
    const entries: VariableChange[] = [];

    for (const variable of variables) {
      const saved = savedMap.get(variable.id);
      if (!saved) {
        entries.push({
          type: "add",
          groupName: "",
          variableId: variable.id,
          fullKey: variable.key,
        });
        continue;
      }
      if (saved.key !== variable.key) {
        entries.push({
          type: "update",
          groupName: "",
          variableId: variable.id,
          fullKey: variable.key,
          field: "key",
          oldValue: saved.key,
          newValue: variable.key,
        });
      }
      if (saved.value !== variable.value) {
        entries.push({
          type: "update",
          groupName: "",
          variableId: variable.id,
          fullKey: variable.key,
          field: "value",
          oldValue: saved.value,
          newValue: variable.value,
        });
      }
    }

    return entries;
  }, [variables, savedVariables]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!release?.id) {
      setIsLoading(false);
      return;
    }

    const releaseVariablesRaw = (release as unknown as { variables?: unknown }).variables;
    const hasVariablesField = Array.isArray(releaseVariablesRaw);

    if (loadedReleaseIdRef.current !== release.id) {
      loadedReleaseIdRef.current = release.id;
      loadedVariablesFieldRef.current = hasVariablesField;
      if (hasVariablesField) {
        syncVariablesFromRelease(releaseVariablesRaw);
        setIsLoading(false);
        fetchReleaseVariables(release.id, { showLoading: false });
      } else {
        setVariables([]);
        setSavedVariables([]);
        fetchReleaseVariables(release.id, { showLoading: true });
      }
      return;
    }

    if (!loadedVariablesFieldRef.current && hasVariablesField && !isDirtyRef.current) {
      loadedVariablesFieldRef.current = true;
      syncVariablesFromRelease(releaseVariablesRaw);
      setIsLoading(false);
    }
  }, [fetchReleaseVariables, release?.id, (release as unknown as { variables?: unknown })?.variables, syncVariablesFromRelease]);

  const validationErrors = useMemo(() => {
    const errors: VariableValidationError[] = [];
    const keyToIds = new Map<string, string[]>();

    for (const v of variables) {
      const keyError = validateKey(v.key);
      if (keyError) {
        errors.push({
          variableId: v.id,
          groupName: "",
          fullKey: v.key,
          field: "key",
          message: keyError,
        });
      }
      const valueError = validateValue(v.value);
      if (valueError) {
        errors.push({
          variableId: v.id,
          groupName: "",
          fullKey: v.key,
          field: "value",
          message: valueError,
        });
      }
      if (v.key.trim()) {
        const ids = keyToIds.get(v.key) || [];
        ids.push(v.id);
        keyToIds.set(v.key, ids);
      }
    }

    for (const [key, ids] of keyToIds.entries()) {
      if (ids.length > 1) {
        for (const variableId of ids) {
          const hasKeyError = errors.some((e) => e.variableId === variableId && e.field === "key");
          if (!hasKeyError) {
            errors.push({
              variableId,
              groupName: "",
              fullKey: key,
              field: "key",
              message: `Duplicate key "${key}"`,
            });
          }
        }
      }
    }

    return errors;
  }, [variables]);

  const hasValidationErrors = validationErrors.length > 0;

  const getVariableIsDirty = useCallback(
    (variableId: string): boolean => {
      const variable = variables.find((v) => v.id === variableId);
      const saved = savedVariables.find((v) => v.id === variableId);
      if (!variable) {
        return false;
      }
      if (!saved) {
        return true;
      }
      return variable.key !== saved.key || variable.value !== saved.value;
    },
    [variables, savedVariables],
  );

  const getVariableErrors = useCallback(
    (variableId: string): VariableValidationError[] => {
      return validationErrors.filter((e) => e.variableId === variableId);
    },
    [validationErrors],
  );

  const addVariable = useCallback(() => {
    const now = new Date().toISOString();
    const newVariable: Variable = {
      id: uuid(),
      key: "",
      value: "",
      created: now,
      lastModified: now,
    };
    setVariables((prev) => [...prev, newVariable]);
  }, []);

  const updateVariable = useCallback((variableId: string, updates: { key?: string; value?: string }) => {
    setVariables((prev) =>
      prev.map((v) => {
        if (v.id !== variableId) {
          return v;
        }
        return {
          ...v,
          ...(updates.key !== undefined && { key: updates.key }),
          ...(updates.value !== undefined && { value: updates.value }),
          lastModified: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const deleteVariable = useCallback(
    (variableId: string) => {
      const isNew = !savedVariables.some((v) => v.id === variableId);
      if (isNew) {
        setVariables((prev) => prev.filter((v) => v.id !== variableId));
        return;
      }
      if (!release?.id) {
        return;
      }
      setSavingVariableId(variableId);
      deleteReleaseVariable
        .submit({
          params: {
            releaseId: release.id,
            variableId,
          },
        })
        .then(() => {
          setVariables((prev) => prev.filter((v) => v.id !== variableId));
          setSavedVariables((prev) => prev.filter((v) => v.id !== variableId));
          notify.success(t("variables.notifications.deleted"));
        })
        .catch(() => {
          notify.error(t("variables.notifications.delete_failed"));
        })
        .finally(() => {
          setSavingVariableId((current) => (current === variableId ? null : current));
        });
    },
    [notify, release?.id, savedVariables, t],
  );

  const saveVariable = useCallback(
    (variableId: string): Promise<{ success: boolean; error?: string }> => {
      const variable = variables.find((v) => v.id === variableId);
      if (!variable) {
        return Promise.resolve({ success: false, error: "Variable not found" });
      }
      const keyError = validateKey(variable.key);
      const valueError = validateValue(variable.value);
      const validationError = keyError ?? valueError ?? null;
      if (validationError) {
        notify.error(validationError);
        return Promise.resolve({ success: false, error: validationError });
      }
      const duplicateKey = variables.filter((v) => v.key === variable.key && v.id !== variableId);
      if (duplicateKey.length > 0) {
        notify.error(t("variables.notifications.duplicate_key"));
        return Promise.resolve({ success: false, error: "Duplicate key" });
      }
      if (!release?.id) {
        notify.error(t("variables.notifications.no_release"));
        return Promise.resolve({ success: false, error: "No release selected" });
      }

      const isNew = !savedVariables.some((v) => v.id === variableId);
      setSavingVariableId(variableId);

      return new Promise((resolve) => {
        const request = isNew
          ? createReleaseVariable.submit({
              params: { releaseId: release.id },
              body: {
                id: variable.id,
                key: variable.key,
                value: variable.value,
              },
            })
          : updateReleaseVariable.submit({
              params: { releaseId: release.id, variableId },
              body: {
                key: variable.key,
                value: variable.value,
              },
            });

        request
          .then((response) => {
            const responseData = response?.data as Partial<Variable> | undefined;
            const responseLooksLikeVariable =
              Boolean(responseData) &&
              typeof responseData === "object" &&
              "key" in responseData &&
              "value" in responseData;
            const savedVariable = responseLooksLikeVariable
              ? { ...variable, ...responseData }
              : { ...variable };

            setVariables((prev) =>
              prev.map((v) => (v.id === variableId ? { ...v, ...savedVariable } : v)),
            );
            setSavedVariables((prev) =>
              isNew
                ? [...prev.filter((v) => v.id !== variableId), { ...savedVariable }]
                : prev.map((v) => (v.id === variableId ? { ...savedVariable } : v)),
            );
            notify.success(
              isNew
                ? t("variables.notifications.saved")
                : t("variables.notifications.modified"),
            );
            resolve({ success: true });
          })
          .catch(() => {
            notify.error(t("variables.notifications.save_failed"));
            resolve({ success: false, error: "Failed to save variable" });
          })
          .finally(() => {
            setSavingVariableId((current) => (current === variableId ? null : current));
          });
      });
    },
    [notify, release?.id, savedVariables, t, variables],
  );

  const value: VariablesContextValue = {
    variables,
    addVariable,
    updateVariable,
    deleteVariable,
    saveVariable,
    isDirty,
    changes,
    getVariableIsDirty,
    getVariableErrors,
    validationErrors,
    hasValidationErrors,
    isSavingVariableId: savingVariableId,
    isLoading,
  };

  return <VariablesContext.Provider value={value}>{children}</VariablesContext.Provider>;
};

//#endregion

export default VariablesProvider;
